import i18n from 'helpers/i18n';
import messages from './messages';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';

interface SubscriptionProps {
  subscription: {
    id: any;
    name: string;
    product: { display_name: string; name: string };
    end_date: string;
    max_seats: number;
    users: number;
    seats_used?: number;
  };
}

interface Variant {
  icon: string;
  iconBgColor: string;
  topBorder: string;
}

const variants: { [key: string]: Variant } = {
  learning_resources: {
    icon: 'fa-graduation-cap',
    iconBgColor: 'bg-purple-100',
    topBorder: 'bg-purple-500'
  },
  data_collection: {
    icon: 'fa-clipboard',
    iconBgColor: 'bg-orange-100',
    topBorder: 'bg-orange-500'
  },
  class_connect: {
    icon: 'fa-people-roof',
    iconBgColor: 'bg-blue-100',
    topBorder: 'bg-blue-500'
  },
  instant_insights: {
    icon: 'fa-lightbulb-on',
    iconBgColor: 'bg-navy-100',
    topBorder: 'bg-navy-500'
  }
};

const subscriptionCountLabels: { [key: string]: string } = {
  learning_resources: i18n.ft(messages.educatorSubscriptions),
  data_collection: i18n.ft(messages.classrooms),
  class_connect: i18n.ft(messages.seats)
};

function SubscriptionCard({ subscription }: SubscriptionProps) {
  const seatsUsed = subscription.seats_used ?? subscription.users;
  const variant: Variant = variants[subscription.product.name];
  const subscriptionCountLabel =
    subscriptionCountLabels[subscription.product.name];

  return (
    <div className="shadow-md rounded-b-lg">
      <div className={clsx('h-4 rounded-t-lg', variant.topBorder)} />

      <Link
        className="block p-4 text-black hover:text-black rounded-b-lg"
        to={`/organization/subscriptions/${subscription.id}/users`}
      >
        <div className="flex justify-between items-center">
          <div className="text-base font-semibold text-charcoal-500">
            {subscription.product.display_name}
          </div>
          <div
            className={clsx(
              'rounded-full size-10 flex justify-center items-center',
              variant.iconBgColor
            )}
          >
            <i className={clsx('fa-solid text-xl', variant.icon)} />
          </div>
        </div>

        <div className="mb-4 text-xl font-bold">{subscription.name}</div>

        <div className="mb-4 flex justify-between">
          <span className="font-semibold">{i18n.ft(messages.expiresOn)}:</span>
          <span>{subscription.end_date || '--'}</span>
        </div>

        <div className="flex flex-col gap-2">
          <div className="font-semibold text-sm text-charcoal-500">
            {subscriptionCountLabel}
          </div>

          {subscription.max_seats && (
            <div className="flex justify-between">
              <span className="font-semibold">
                {i18n.ft(messages.available)}:
              </span>
              {subscription.max_seats - seatsUsed}
            </div>
          )}

          <div className="flex justify-between">
            <span className="font-semibold">{i18n.ft(messages.used)}:</span>
            {seatsUsed}
          </div>

          {subscription.max_seats && (
            <div className="flex justify-between">
              <span className="font-semibold">{i18n.ft(messages.total)}:</span>
              {subscription.max_seats}
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}

export default SubscriptionCard;
